import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface RecruiterPerformance {
  recruiterId: string;
  recruiterName: string;
  recruiterEmail: string;
  openPositions: number;
  totalCandidates: number;
  candidatesSourced: number;
  candidatesInterviewed: number;
  candidatesSelected: number;
  candidatesRejected: number;
  conversionRate: number;
  interviewRate: number;
  selectionRate: number;
  avgTimeToHire: number | null;
  funnelData: FunnelStage[];
}

export interface FunnelStage {
  stageName: string;
  stageType: string;
  count: number;
  color: string;
}

export interface PerformanceData {
  recruiters: RecruiterPerformance[];
  teamAverages: {
    openPositions: number;
    totalCandidates: number;
    conversionRate: number;
    interviewRate: number;
    selectionRate: number;
    avgTimeToHire: number | null;
  };
  totalMetrics: {
    totalJobs: number;
    totalCandidates: number;
    totalInterviewed: number;
    totalSelected: number;
  };
}

interface Recruiter {
  id: string;
  full_name: string;
  email: string;
}

export function usePerformanceData(
  dateRange: { from: Date | null; to: Date | null },
  selectedRecruiterId: string | null
) {
  const [data, setData] = useState<PerformanceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedRecruiterId]);

  const fetchData = async () => {
    setIsLoading(true);

    try {
      // Fetch all app_users (recruiters)
      const { data: usersData, error: usersError } = await supabase
        .from('app_users')
        .select('id, full_name, email')
        .eq('is_active', true);

      if (usersError) throw usersError;
      setRecruiters(usersData || []);

      // Fetch jobs with assigned recruiters
      let jobsQuery = supabase
        .from('jobs')
        .select('id, title, status, assigned_to, created_at, updated_at');

      if (dateRange.from) {
        jobsQuery = jobsQuery.gte('created_at', dateRange.from.toISOString());
      }
      if (dateRange.to) {
        jobsQuery = jobsQuery.lte('created_at', dateRange.to.toISOString());
      }

      const { data: jobsData, error: jobsError } = await jobsQuery;
      if (jobsError) throw jobsError;

      // Fetch candidate applications
      let applicationsQuery = supabase
        .from('candidate_applications')
        .select(`
          id,
          candidate_id,
          job_id,
          stage_id,
          status,
          applied_at,
          job_stages (
            id,
            name,
            stage_type,
            color
          )
        `);

      if (dateRange.from) {
        applicationsQuery = applicationsQuery.gte('applied_at', dateRange.from.toISOString());
      }
      if (dateRange.to) {
        applicationsQuery = applicationsQuery.lte('applied_at', dateRange.to.toISOString());
      }

      const { data: applicationsData, error: applicationsError } = await applicationsQuery;
      if (applicationsError) throw applicationsError;

      // Build a map of job_id -> recruiter_id
      const jobRecruiterMap = new Map<string, string>();
      (jobsData || []).forEach((job: any) => {
        if (job.assigned_to) {
          jobRecruiterMap.set(job.id, job.assigned_to);
        }
      });

      // Build recruiter performance data
      const recruiterMap = new Map<string, RecruiterPerformance>();

      // Initialize recruiters
      (usersData || []).forEach((user: any) => {
        recruiterMap.set(user.id, {
          recruiterId: user.id,
          recruiterName: user.full_name,
          recruiterEmail: user.email,
          openPositions: 0,
          totalCandidates: 0,
          candidatesSourced: 0,
          candidatesInterviewed: 0,
          candidatesSelected: 0,
          candidatesRejected: 0,
          conversionRate: 0,
          interviewRate: 0,
          selectionRate: 0,
          avgTimeToHire: null,
          funnelData: [],
        });
      });

      // Count open positions per recruiter
      (jobsData || []).forEach((job: any) => {
        if (job.assigned_to && job.status !== 'closed') {
          const recruiter = recruiterMap.get(job.assigned_to);
          if (recruiter) {
            recruiter.openPositions++;
          }
        }
      });

      // Process applications per recruiter
      const recruiterFunnels = new Map<string, Map<string, { count: number; stageName: string; stageType: string; color: string }>>();

      (applicationsData || []).forEach((app: any) => {
        const recruiterId = jobRecruiterMap.get(app.job_id);
        if (!recruiterId) return;

        const recruiter = recruiterMap.get(recruiterId);
        if (!recruiter) return;

        recruiter.totalCandidates++;
        recruiter.candidatesSourced++;

        // Process stage data
        const stageType = app.job_stages?.stage_type || app.status || 'applied';
        const stageName = app.job_stages?.name || app.status || 'Applied';
        const stageColor = app.job_stages?.color || '#6366f1';

        // Track funnel data
        if (!recruiterFunnels.has(recruiterId)) {
          recruiterFunnels.set(recruiterId, new Map());
        }
        const funnelMap = recruiterFunnels.get(recruiterId)!;
        if (!funnelMap.has(stageType)) {
          funnelMap.set(stageType, { count: 0, stageName, stageType, color: stageColor });
        }
        funnelMap.get(stageType)!.count++;

        // Count interview and selection
        if (['interview', 'technical_interview', 'final_interview', 'interviewed'].includes(stageType)) {
          recruiter.candidatesInterviewed++;
        }
        if (['offer', 'hired', 'selected', 'accepted'].includes(stageType)) {
          recruiter.candidatesSelected++;
        }
        if (['rejected', 'declined'].includes(stageType)) {
          recruiter.candidatesRejected++;
        }
      });

      // Calculate rates and build funnel data
      recruiterMap.forEach((recruiter, recruiterId) => {
        if (recruiter.totalCandidates > 0) {
          recruiter.interviewRate = (recruiter.candidatesInterviewed / recruiter.totalCandidates) * 100;
          recruiter.selectionRate = (recruiter.candidatesSelected / recruiter.totalCandidates) * 100;
          recruiter.conversionRate = recruiter.selectionRate;
        }

        // Build funnel data
        const funnelMap = recruiterFunnels.get(recruiterId);
        if (funnelMap) {
          recruiter.funnelData = Array.from(funnelMap.values()).sort((a, b) => {
            const order = ['sourced', 'applied', 'screening', 'interview', 'technical_interview', 'final_interview', 'offer', 'hired', 'rejected'];
            return order.indexOf(a.stageType) - order.indexOf(b.stageType);
          });
        }
      });

      // Calculate team averages
      const allRecruiters = Array.from(recruiterMap.values()).filter(r => r.totalCandidates > 0);
      const teamAverages = {
        openPositions: allRecruiters.reduce((sum, r) => sum + r.openPositions, 0) / (allRecruiters.length || 1),
        totalCandidates: allRecruiters.reduce((sum, r) => sum + r.totalCandidates, 0) / (allRecruiters.length || 1),
        conversionRate: allRecruiters.reduce((sum, r) => sum + r.conversionRate, 0) / (allRecruiters.length || 1),
        interviewRate: allRecruiters.reduce((sum, r) => sum + r.interviewRate, 0) / (allRecruiters.length || 1),
        selectionRate: allRecruiters.reduce((sum, r) => sum + r.selectionRate, 0) / (allRecruiters.length || 1),
        avgTimeToHire: null,
      };

      const totalMetrics = {
        totalJobs: jobsData?.length || 0,
        totalCandidates: applicationsData?.length || 0,
        totalInterviewed: allRecruiters.reduce((sum, r) => sum + r.candidatesInterviewed, 0),
        totalSelected: allRecruiters.reduce((sum, r) => sum + r.candidatesSelected, 0),
      };

      setData({
        recruiters: Array.from(recruiterMap.values()),
        teamAverages,
        totalMetrics,
      });
    } catch (error) {
      console.error('Error fetching performance data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return { data, isLoading, recruiters };
}
